import type { OrderFilters, OrderStatus, PaymentMethod, OrderType } from "@cold-crew/shared";
import { Search, X, SlidersHorizontal } from "lucide-react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

interface Producto { id: number; nombre: string; }

interface OrderFiltersBarProps {
  filters:  Partial<OrderFilters>;
  onChange: (next: Partial<OrderFilters>) => void;
}

const STATUS_OPTIONS: { value: OrderStatus | ""; label: string }[] = [
  { value: "",          label: "Todos los estados" },
  { value: "pendiente", label: "Pendiente"         },
  { value: "entregado", label: "Entregado"         },
  { value: "cancelado", label: "Cancelado"         },
];

const TIPO_OPTIONS: { value: OrderType | ""; label: string }[] = [
  { value: "",       label: "Todo tipo"  },
  { value: "venta",  label: "🛒 Venta"  },
  { value: "regalo", label: "🎁 Regalo" },
];

const PAYMENT_OPTIONS: { value: PaymentMethod | ""; label: string }[] = [
  { value: "",              label: "Todo pago"     },
  { value: "efectivo",      label: "Efectivo"      },
  { value: "transferencia", label: "Transferencia" },
  { value: "tarjeta",       label: "Tarjeta"       },
  { value: "n/a",           label: "N/A (Regalo)"  },
  { value: "otro",          label: "Otro"          },
];

const base = "h-10 w-full rounded-xl border border-input bg-card px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors";

export function OrderFiltersBar({ filters, onChange }: OrderFiltersBarProps) {
  const [open, setOpen] = useState(false);
  const hasActive = !!(filters.search || filters.status || filters.tipo_pedido
    || filters.metodo_pago || filters.date_from || filters.date_to || filters.producto_id);

  const { data: productosData } = useQuery({
    queryKey: ["productos-bebida"],
    queryFn:  () => api.get<{ data: Producto[]; total: number }>("/inventario/productos", { categoria: "bebida", activo: "1", limit: "50" }),
    staleTime: 5 * 60 * 1000,
  });
  const productos: Producto[] = Array.isArray(productosData)
    ? productosData
    : productosData?.data ?? [];

  function clear() { onChange({ page: 1, limit: filters.limit ?? 20 }); }

  return (
    <div className="space-y-2">
      {/* Search + toggle */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
          <input type="search" placeholder="Cliente o # pedido…"
            value={filters.search ?? ""}
            onChange={(e) => onChange({ ...filters, search: e.target.value || undefined, page: 1 })}
            className={cn(base, "pl-9")} />
        </div>
        {/* Botón Filtros — activo usa el mismo degradado del Hero/Sidebar */}
        <button onClick={() => setOpen((v) => !v)}
          style={(open || hasActive) ? { background: "#090909" } : undefined}
          className={cn(
            "flex items-center gap-1.5 px-3 h-10 rounded-xl border text-sm font-medium transition-colors shrink-0",
            open || hasActive
              ? "text-white border-transparent"
              : "border-input bg-card text-muted-foreground hover:text-foreground"
          )}>
          <SlidersHorizontal className="size-4" />
          <span className="hidden sm:inline">Filtros</span>
          {hasActive && <span className="size-2 rounded-full bg-[#FF6500]" />}
        </button>
      </div>

      {/* Filtros expandibles */}
      {open && (
        <div className="space-y-2 p-3 rounded-xl border border-border bg-card/60">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1 block">Tipo pedido</label>
              <select value={filters.tipo_pedido ?? ""}
                onChange={(e) => onChange({ ...filters, tipo_pedido: (e.target.value as OrderType) || undefined, page: 1 })}
                className={cn(base, "pr-8 cursor-pointer")}>
                {TIPO_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1 block">Estado</label>
              <select value={filters.status ?? ""}
                onChange={(e) => onChange({ ...filters, status: (e.target.value as OrderStatus) || undefined, page: 1 })}
                className={cn(base, "pr-8 cursor-pointer")}>
                {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          {/* Filtro por producto */}
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1 block">Producto</label>
            <select value={filters.producto_id ?? ""}
              onChange={(e) => onChange({ ...filters, producto_id: e.target.value ? Number(e.target.value) : undefined, page: 1 })}
              className={cn(base, "pr-8 cursor-pointer")}>
              <option value="">Todos los productos</option>
              {productos.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </select>
          </div>

          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1 block">Método de pago</label>
            <select value={filters.metodo_pago ?? ""}
              onChange={(e) => onChange({ ...filters, metodo_pago: (e.target.value as PaymentMethod) || undefined, page: 1 })}
              className={cn(base, "pr-8 cursor-pointer")}>
              {PAYMENT_OPTIONS
                .filter((o) => filters.tipo_pedido !== "venta" || o.value !== "n/a")
                .map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1 block">Desde</label>
              <input type="date" value={filters.date_from ?? ""}
                onChange={(e) => onChange({ ...filters, date_from: e.target.value || undefined, page: 1 })}
                className={base} />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1 block">Hasta</label>
              <input type="date" value={filters.date_to ?? ""}
                onChange={(e) => onChange({ ...filters, date_to: e.target.value || undefined, page: 1 })}
                className={base} />
            </div>
          </div>

          {hasActive && (
            <button onClick={clear}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
              <X className="size-3" /> Limpiar filtros
            </button>
          )}
        </div>
      )}
    </div>
  );
}

